import { createFileRoute, Link } from "@tanstack/react-router";
import { Icon } from "@/components/Icon";

import { Button } from "@/components/ui/button";
import { Reveal } from "@/components/Reveal";
import { MobileStickyBar } from "@/components/MobileStickyBar";
import { AGENCY } from "@/lib/agency";
import { buildHead } from "@/lib/seo";

export const Route = createFileRoute("/contact")({
  head: () =>
    buildHead({
      title: `Contact ${AGENCY.name} - Talk to a Business Loan Advisor`,
      description: `Call ${AGENCY.phone} to speak with a ${AGENCY.short} advisor. Serving ${AGENCY.area}. No upfront fees, no obligation.`,
      path: "/contact",
      breadcrumbs: [
        { name: "Home", path: "/" },
        { name: "Contact", path: "/contact" },
      ],
    }),
  component: ContactPage,
});

const eyebrow = "text-xs font-semibold uppercase tracking-[0.18em]";

function ContactPage() {
  return (
    <>
      <Hero />
      <ContactAndMap />
      <Reassurance />
      <FinalCTA />
      <MobileStickyBar applyHref="/apply-funding" />
    </>
  );
}

/* ---------------- Hero ---------------- */

function Hero() {
  return (
    <section className="relative overflow-hidden bg-primary text-primary-foreground">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-70"
        style={{
          background:
            "radial-gradient(60% 50% at 15% 20%, color-mix(in oklab, var(--accent) 22%, transparent), transparent 70%), radial-gradient(45% 45% at 85% 80%, color-mix(in oklab, var(--accent) 14%, transparent), transparent 70%)",
        }}
      />
      <div className="relative mx-auto w-full max-w-7xl px-6 py-20 text-center md:py-28 lg:px-10">
        <Reveal>
          <p className={`${eyebrow} flex items-center justify-center gap-2 text-accent`}>
            <span className="h-px w-8 bg-current opacity-60" /> Contact us
          </p>
          <h1 className="mx-auto mt-5 max-w-4xl font-serif text-5xl font-semibold leading-[1.02] tracking-tight md:text-6xl lg:text-7xl">
            Let's <span className="text-accent">talk</span> about your financing.
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-lg leading-relaxed text-primary-foreground/80 md:text-xl">
            The fastest way to a real answer is a quick call. You'll reach a
            live advisor, not a queue. Tell us about your business and we'll
            point you to the right program in minutes.
          </p>
          <div className="mt-9 flex flex-wrap items-center justify-center gap-3">
            <Button
              asChild
              size="lg"
              className="rounded-full bg-accent px-7 text-accent-foreground hover:bg-accent/90"
            >
              <a href={AGENCY.phoneHref}>
                <Icon name="phonecall" color="bl" className="h-4 w-4 brightness-0 invert"  /> Call {AGENCY.phone}
              </a>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="rounded-full border-primary-foreground/30 bg-transparent px-6 text-primary-foreground hover:bg-primary-foreground/10 hover:text-primary-foreground"
            >
              <Link to="/apply-funding">Apply Now</Link>
            </Button>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* ---------------- Contact + Map ---------------- */

function ContactAndMap() {
  const items = [
    {
      icon: "phonecall",
      label: "Phone",
      value: AGENCY.phone,
      href: AGENCY.phoneHref,
      sub: "Live advisors during business hours.",
    },
    {
      icon: "clock",
      label: "Hours",
      value: "Mon-Fri · 8:00 AM - 7:00 PM CT",
      sub: "Saturday · 9:00 AM - 2:00 PM CT",
    },
    {
      icon: "mappin",
      label: "Office",
      value: AGENCY.address,
      sub: "By appointment.",
    },
    {
      icon: "mappin",
      label: "Service area",
      value: `Serving ${AGENCY.area}`,
      sub: "Dallas, Fort Worth, Plano, Arlington, Irving, Frisco, Garland, and surrounding cities.",
    },
  ];

  const mapQuery = encodeURIComponent(AGENCY.address);
  const mapSrc = `https://www.google.com/maps?q=${mapQuery}&output=embed`;

  return (
    <section className="relative overflow-hidden border-b border-border bg-background">
      <div className="relative mx-auto w-full max-w-7xl px-6 py-20 md:py-28 lg:px-10">
        <div className="max-w-3xl">
          <p className={`${eyebrow} flex items-center gap-2 text-accent`}>
            <span className="h-px w-8 bg-current opacity-60" /> Get in touch
          </p>
          <h2 className="mt-5 font-serif text-4xl font-semibold leading-[1.05] tracking-tight md:text-5xl">
            One call. One advisor.{" "}
            <span className="text-accent">Straight answers.</span>
          </h2>
        </div>

        <div className="mt-14 grid gap-8 lg:grid-cols-12">
          {/* Contact details */}
          <div className="lg:col-span-5">
            <Reveal>
              <div className="rounded-3xl border border-border bg-card p-6 shadow-sm md:p-8">
                <ul className="space-y-6">
                  {items.map((it) => (
                    <li key={it.label} className="flex gap-4">
                      <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-accent/15 text-accent">
                        <Icon name={it.icon} color="co" className="h-5 w-5"  />
                      </span>
                      <div className="min-w-0">
                        <p className={`${eyebrow} text-muted-foreground`}>
                          {it.label}
                        </p>
                        {it.href ? (
                          <a
                            href={it.href}
                            className="mt-1 block break-words font-serif text-lg font-semibold text-foreground hover:text-accent"
                          >
                            {it.value}
                          </a>
                        ) : (
                          <p className="mt-1 break-words font-serif text-lg font-semibold text-foreground">
                            {it.value}
                          </p>
                        )}
                        {it.sub ? (
                          <p className="mt-1 text-sm text-muted-foreground">
                            {it.sub}
                          </p>
                        ) : null}
                      </div>
                    </li>
                  ))}
                </ul>

                <div className="mt-8 border-t border-border pt-6">
                  <Button
                    asChild
                    size="lg"
                    className="w-full rounded-full bg-accent text-accent-foreground hover:bg-accent/90"
                  >
                    <a href={AGENCY.phoneHref}>
                      <Icon name="phonecall" color="bl" className="h-4 w-4 brightness-0 invert"  /> Call {AGENCY.phone}
                    </a>
                  </Button>
                  <p className="mt-3 text-center text-xs text-muted-foreground">
                    No upfront fees. No obligation. No hard credit pull to
                    start the conversation.
                  </p>
                </div>
              </div>
            </Reveal>
          </div>

          {/* Map */}
          <div className="lg:col-span-7">
            <Reveal delay={100}>
              <div className="relative">
                <div className="absolute -inset-3 -z-10 rounded-3xl bg-accent/10 blur-2xl" />
                <div className="overflow-hidden rounded-3xl border border-border bg-card shadow-sm">
                  <iframe
                    title={`Map showing ${AGENCY.name} service area`}
                    src={mapSrc}
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    className="h-[420px] w-full md:h-[560px]"
                    allowFullScreen
                  />
                </div>
                <div className="mt-4 flex flex-wrap items-center justify-between gap-3 text-sm text-muted-foreground">
                  <p className="inline-flex items-center gap-2">
                    <Icon name="mappin" color="bl" className="h-4 w-4 text-accent"  />
                    {AGENCY.address}
                  </p>
                  <a
                    href={`https://www.google.com/maps/search/?api=1&query=${mapQuery}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-semibold text-accent hover:underline"
                  >
                    Get directions →
                  </a>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------------- Reassurance ---------------- */

function Reassurance() {
  const items = [
    {
      icon: "building2",
      title: "Operating entity",
      body: `${AGENCY.name} is an independent business financing advisory headquartered in ${AGENCY.city}. We are not a direct lender.`,
    },
    {
      icon: "mappin",
      title: "Where we work",
      body: `We serve ${AGENCY.area} and surrounding North Texas communities.`,
    },
    {
      icon: "shieldcheck",
      title: "Loan approvals",
      body: "All loans are subject to lender approval and underwriting. Rates, terms, fees, and product availability vary by lender and depend on your business profile, credit, time in business, and revenue.",
    },
  ];

  return (
    <section className="relative overflow-hidden border-b border-border bg-secondary">
      <div className="relative mx-auto w-full max-w-7xl px-6 py-16 md:py-20 lg:px-10">
        <div className="grid gap-6 md:grid-cols-3">
          {items.map((it, i) => (
            <Reveal key={it.title} delay={i * 80}>
              <div className="flex h-full gap-4 rounded-2xl border border-border bg-card p-6">
                <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-accent/15 text-accent">
                  <Icon name={it.icon} color="co" className="h-5 w-5"  />
                </span>
                <div>
                  <h3 className="font-serif text-lg font-semibold tracking-tight">
                    {it.title}
                  </h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">
                    {it.body}
                  </p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------- Final CTA ---------------- */

function FinalCTA() {
  return (
    <section
      id="apply"
      className="relative overflow-hidden border-b border-border bg-primary text-primary-foreground"
    >
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-70"
        style={{
          background:
            "radial-gradient(60% 50% at 15% 20%, color-mix(in oklab, var(--accent) 22%, transparent), transparent 70%), radial-gradient(45% 45% at 85% 80%, color-mix(in oklab, var(--accent) 14%, transparent), transparent 70%)",
        }}
      />
      <div
        aria-hidden
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(120deg, color-mix(in oklab, var(--primary) 92%, transparent), color-mix(in oklab, var(--primary) 70%, transparent))",
        }}
      />
      <div className="relative mx-auto max-w-7xl px-6 py-20 md:py-28 lg:px-10">
        <div className="max-w-3xl">
          <p className={`${eyebrow} flex items-center gap-2 text-accent`}>
            <span className="h-px w-8 bg-current opacity-60" /> Ready when you are
          </p>
          <h2 className="mt-5 font-serif text-4xl font-semibold leading-[1.05] tracking-tight md:text-5xl lg:text-6xl">
            Talk to an advisor today.
          </h2>
          <p className="mt-5 max-w-2xl text-lg text-primary-foreground/80">
            Call {AGENCY.phone} or start your application online. Most owners
            get a clear next step the same day.
          </p>
          <div className="mt-9 flex flex-wrap items-center gap-3">
            <Button
              asChild
              size="lg"
              className="rounded-full bg-accent px-7 text-accent-foreground hover:bg-accent/90"
            >
              <Link to="/apply-funding">Apply Now</Link>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="rounded-full border-primary-foreground/30 bg-transparent px-6 text-primary-foreground hover:bg-primary-foreground/10 hover:text-primary-foreground"
            >
              <a href={AGENCY.phoneHref}>
                <Icon name="phonecall" color="bl" className="h-4 w-4 brightness-0 invert"  /> Call {AGENCY.phone}
              </a>
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
}
